import React, { useState } from 'react';
import {
  Card,
  Table,
  Button,
  Input,
  Select,
  Space,
  Tag,
  Modal,
  Descriptions,
  Tabs,
  Row,
  Col,
  Statistic,
  Progress,
  Avatar,
  Badge,
  Tooltip,
  Divider,
  message,
  Popconfirm,
  List,
  Upload,
  Alert,
  Steps,
  Radio,
  Switch,
  InputNumber,
  Form,
  Checkbox,
} from 'antd';
import {
  SearchOutlined,
  EyeOutlined,
  EditOutlined,
  UserOutlined,
  TagOutlined,
  BarChartOutlined,
  PieChartOutlined,
  LineChartOutlined,
  ReloadOutlined,
  ExportOutlined,
  ImportOutlined,
  FilterOutlined,
  PlusOutlined,
  DeleteOutlined,
  DownloadOutlined,
  UploadOutlined,
  FileTextOutlined,
  RobotOutlined,
} from '@ant-design/icons';

const { Option } = Select;

const UserProfile: React.FC = () => {
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState<any>(null);
  const [tagModalVisible, setTagModalVisible] = useState(false);
  const [analysisModalVisible, setAnalysisModalVisible] = useState(false);
  const [importModalVisible, setImportModalVisible] = useState(false);
  const [exportModalVisible, setExportModalVisible] = useState(false);
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [filterGender, setFilterGender] = useState('all');
  const [filterAge, setFilterAge] = useState('all');
  const [filterLocation, setFilterLocation] = useState('all');

  // 置信度显示组件
  const ConfidenceIndicator: React.FC<{ confidence: number; field: string }> = ({ confidence, field }) => {
    const getColor = (conf: number) => {
      if (conf >= 0.9) return '#52c41a'; // 绿色 - 高置信度
      if (conf >= 0.7) return '#faad14'; // 橙色 - 中等置信度
      return '#ff4d4f'; // 红色 - 低置信度
    };

    const getLabel = (conf: number) => {
      if (conf >= 0.9) return '高';
      if (conf >= 0.7) return '中';
      return '低';
    };

    return (
      <Tooltip title={`${field}置信度: ${(confidence * 100).toFixed(1)}%`}>
        <Tag color={getColor(confidence)}>
          {getLabel(confidence)}
        </Tag>
      </Tooltip>
    );
  };

  // 处理函数
  const handleAnalyze = () => {
    setAnalysisModalVisible(true);
    message.loading('正在分析用户行为...', 1);
    setTimeout(() => {
      message.success('用户行为分析完成！');
    }, 2000);
  };

  const handleImport = () => {
    setImportModalVisible(true);
  };

  const handleExport = async () => {
    setLoading(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 1000));
      message.success('用户画像导出成功！');
      setExportModalVisible(true);
    } catch (error) {
      message.error('导出失败，请重试');
    } finally {
      setLoading(false);
    }
  };

  const handleBatchDelete = async () => {
    if (selectedRowKeys.length === 0) {
      message.warning('请选择要删除的用户画像');
      return;
    }
    setLoading(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 1500));
      message.success(`已删除 ${selectedRowKeys.length} 个用户画像`);
      setSelectedRowKeys([]);
    } catch (error) {
      message.error('批量删除失败，请重试');
    } finally {
      setLoading(false);
    }
  };

  // 模拟用户画像数据
  const userProfileData = [
    {
      id: 1,
      userId: 'user_001',
      username: '张三',
      age: '25-30',
      gender: '男',
      location: '北京',
      education: '本科',
      occupation: '程序员',
      interests: ['游戏', '科技', '运动'],
      userLevel: 'VIP1',
      totalDeposit: 5000,
      totalWithdraw: 2000,
      totalBet: 15000,
      onlineTime: '120小时',
      avgSessionTime: '25分钟',
      favoriteGames: ['老虎机', '德州扑克'],
      lastActiveTime: '2025-10-23 14:30',
      registrationTime: '2025-09-15',
      tags: ['高价值用户', '活跃用户', '游戏爱好者'],
      riskLevel: 'low',
      satisfaction: 4.5,
      // 新增置信度信息
      confidence: {
        age: 0.85,
        gender: 0.95,
        location: 0.90,
        education: 0.70,
        occupation: 0.75,
        interests: 0.80,
        riskLevel: 0.88,
        satisfaction: 0.92
      },
      // 新增摘要信息
      summary: {
        personality: '理性分析型用户，喜欢策略性游戏，消费谨慎但持续',
        behavior: '活跃度高，游戏时间集中在晚上8-11点，偏好高风险高回报游戏',
        preferences: '对新技术敏感，喜欢尝试新游戏，重视游戏体验和公平性',
        riskProfile: '低风险用户，有良好的资金管理能力，不会过度消费',
        value: '高价值用户，具有长期消费潜力，推荐个性化服务和VIP待遇'
      },
      // 新增对话Bot统计和总结
      conversationBots: [
        {
          botType: '业务咨询Bot',
          count: 8,
          solvedProblems: ['提现问题', '充值查询', '活动规则', '账户安全'],
          summary: '用户通过业务咨询Bot解决了多个充值提现相关问题，表现出对资金安全和账户管理的重视。主要集中在工作日晚间咨询，显示出谨慎理性的消费态度。'
        },
        {
          botType: '情感陪伴Bot',
          count: 5,
          solvedProblems: ['游戏压力', '情绪疏导', '休息建议'],
          summary: '用户在与情感陪伴Bot的对话中，多次讨论游戏压力和情绪管理话题。对话频率在游戏失利后明显增加，Bot能够有效提供情感支持和休息建议，用户回复积极，表现出较好的心理调节能力。'
        },
        {
          botType: '游戏种草Bot',
          count: 2,
          solvedProblems: ['新游戏推荐', '游戏体验'],
          summary: '用户通过游戏种草Bot了解新游戏，表现出对探索新玩法的兴趣，但咨询频率较低，显示出谨慎试玩的态度。'
        }
      ],
      conversationSummary: `用户张三在最近一周共进行了15次对话，主要与3个不同类型的Bot进行了交互。`
    },
    {
      id: 2,
      userId: 'user_002',
      username: '李四',
      age: '30-35',
      gender: '女',
      location: '上海',
      education: '硕士',
      occupation: '设计师',
      interests: ['艺术', '音乐', '旅游'],
      userLevel: 'VIP2',
      totalDeposit: 8000,
      totalWithdraw: 3000,
      totalBet: 25000,
      onlineTime: '180小时',
      avgSessionTime: '35分钟',
      favoriteGames: ['骰子', '21点'],
      lastActiveTime: '2025-10-23 14:25',
      registrationTime: '2025-08-20',
      tags: ['高价值用户', '稳定用户', '理性玩家'],
      riskLevel: 'low',
      satisfaction: 4.8,
      summary: {
        personality: '优雅品味型用户，注重游戏美学体验，消费理性而有品质',
        behavior: '游戏时间规律，偏好低风险策略游戏，喜欢在周末长时间在线',
        preferences: '对画面精美和音乐优秀的游戏有独特偏好，重视感官体验',
        riskProfile: '低风险用户，资金管理非常理性，会在盈利时及时止盈',
        value: '高价值稳定用户，忠诚度高，适合推荐精品游戏和专属活动'
      },
      conversationBots: [
        {
          botType: '游戏种草Bot',
          count: 10,
          solvedProblems: ['精品游戏推荐', '视觉效果', '音效优化', '游戏品质'],
          summary: '用户经常向游戏种草Bot咨询高品质游戏推荐，特别关注画面和音效。对话集中在周末，展现出对游戏艺术性和感官体验的极高要求，Bot成功推荐了多款画面精美的游戏，用户反馈积极。'
        },
        {
          botType: '业务咨询Bot',
          count: 8,
          solvedProblems: ['周末特惠', '会员权益', '充值优惠', '账户查询'],
          summary: '用户通过业务咨询Bot了解周末活动和会员权益，表现出规律的游戏习惯和稳定的消费能力。咨询问题专业且有针对性，显示出理性消费态度。'
        },
        {
          botType: '情感陪伴Bot',
          count: 4,
          solvedProblems: ['游戏压力', '情绪管理', '休息建议'],
          summary: '偶尔与情感陪伴Bot交流游戏压力和情绪问题，对话简短有效。用户在游戏体验不佳时会寻求情感支持，说明有一定自控能力。'
        }
      ],
      conversationSummary: `用户李四在最近两周共进行了22次对话，与3个不同类型的Bot进行了交互。`
    },
    {
      id: 3,
      userId: 'user_003',
      username: '王五',
      age: '20-25',
      gender: '男',
      location: '广州',
      education: '大专',
      occupation: '销售',
      interests: ['社交', '娱乐', '购物'],
      userLevel: '普通用户',
      totalDeposit: 1000,
      totalWithdraw: 500,
      totalBet: 3000,
      onlineTime: '45小时',
      avgSessionTime: '15分钟',
      favoriteGames: ['老虎机'],
      lastActiveTime: '2025-10-22 20:15',
      registrationTime: '2025-10-10',
      tags: ['新用户', '潜力用户', '社交型'],
      riskLevel: 'medium',
      satisfaction: 3.8,
      summary: {
        personality: '社交活跃型用户，喜欢互动和分享，尝试新鲜事物意愿强',
        behavior: '活跃时段分散，偏好简单易上手的游戏，单次游戏时间较短',
        preferences: '对社交功能和奖励机制感兴趣，喜欢通过游戏结识新朋友',
        riskProfile: '中风险用户，需要引导理性消费，避免冲动投注',
        value: '潜力用户，有成长空间，适合新用户教育和社区活动推荐'
      },
      conversationBots: [
        {
          botType: '业务咨询Bot',
          count: 5,
          solvedProblems: ['新手引导', '充值方法', '提现流程', '账户管理'],
          summary: '用户主要通过业务咨询Bot解决新手常见问题，询问频率高但问题基础，表现出强烈的学习和探索欲望。Bot提供了详细的新手指导和操作步骤，用户上手迅速。'
        },
        {
          botType: '直播间活跃机器人',
          count: 3,
          solvedProblems: ['社交互动', '活动参与', '互动玩法'],
          summary: '用户与直播间活跃机器人积极互动，表现出对社交和娱乐功能的浓厚兴趣。参与度逐渐提高，说明用户在寻找游戏乐趣和社交价值。'
        }
      ],
      conversationSummary: `用户王五在最近一周共进行了8次对话，作为新用户主要与2个不同类型的Bot交互。`
    },
  ];

  const columns = [
    {
      title: '用户信息',
      key: 'userInfo',
      width: 150,
      fixed: 'left' as const,
      render: (_: any, record: any) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Avatar size="small" icon={<UserOutlined />} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 'bold', fontSize: 13 }}>{record.username}</div>
            <div style={{ fontSize: 12, color: '#999' }}>{record.userId}</div>
            <div>
              <Tag color="blue" style={{ fontSize: 11 }}>{record.userLevel}</Tag>
            </div>
          </div>
        </div>
      ),
    },
    {
      title: '基础信息',
      key: 'basicInfo',
      width: 120,
      render: (_: any, record: any) => (
        <div style={{ fontSize: 12 }}>
          <div>
            {record.gender} · {record.age}
          </div>
          <div style={{ color: '#666', marginTop: 4 }}>{record.location}</div>
          <div style={{ color: '#666', marginTop: 2 }}>{record.occupation}</div>
        </div>
      ),
    },
    {
      title: '业务数据',
      key: 'businessData',
      width: 110,
      render: (_: any, record: any) => (
        <div style={{ fontSize: 12 }}>
          <div>充值: ¥{record.totalDeposit.toLocaleString()}</div>
          <div>投注: ¥{record.totalBet.toLocaleString()}</div>
          <div>在线: {record.onlineTime}</div>
        </div>
      ),
    },
    {
      title: '用户标签',
      key: 'tags',
      width: 120,
      render: (_: any, record: any) => (
        <div style={{ maxWidth: 120 }}>
          <Space wrap size={[2, 2]}>
            {record.tags.slice(0, 2).map((tag: string, index: number) => (
              <Tag key={index} color="green" style={{ fontSize: 10 }}>{tag}</Tag>
            ))}
            {record.tags.length > 2 && (
              <Tag color="default" style={{ fontSize: 10 }}>+{record.tags.length - 2}</Tag>
            )}
          </Space>
        </div>
      ),
    },
    {
      title: '风险等级',
      dataIndex: 'riskLevel',
      key: 'riskLevel',
      width: 80,
      render: (riskLevel: string) => {
        const colors = { low: 'green', medium: 'orange', high: 'red' };
        const labels = { low: '低风险', medium: '中风险', high: '高风险' };
        return <Tag color={colors[riskLevel as keyof typeof colors]} style={{ fontSize: 11 }}>{labels[riskLevel as keyof typeof labels]}</Tag>;
      },
    },
    {
      title: '满意度',
      dataIndex: 'satisfaction',
      key: 'satisfaction',
      width: 90,
      render: (satisfaction: number) => (
        <div>
          <Progress
            percent={satisfaction * 20}
            size="small"
            format={() => `${satisfaction}/5`}
            style={{ fontSize: 11 }}
          />
        </div>
      ),
    },
    {
      title: '操作',
      key: 'action',
      width: 100,
      fixed: 'right' as const,
      render: (_: any, record: any) => (
        <Space>
          <Button
            type="link"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => {
              setSelectedRecord(record);
              setDetailModalVisible(true);
            }}
          >
            查看
          </Button>
        </Space>
      ),
    },
  ];

  const rowSelection = {
    selectedRowKeys,
    onChange: setSelectedRowKeys,
  };

  const profileStats = [
    { title: '总用户数', value: 12580, icon: <UserOutlined /> },
    { title: '活跃用户', value: 3240, icon: <UserOutlined /> },
    { title: 'VIP用户', value: 856, icon: <UserOutlined /> },
    { title: '新用户', value: 156, icon: <UserOutlined /> },
  ];

  const tagStats = [
    { tag: '高价值用户', count: 1256, color: 'gold' },
    { tag: '活跃用户', count: 3240, color: 'green' },
    { tag: '新用户', count: 156, color: 'blue' },
    { tag: '流失风险', count: 89, color: 'red' },
    { tag: '游戏爱好者', count: 2156, color: 'purple' },
    { tag: '理性玩家', count: 1890, color: 'cyan' },
  ];

  return (
    <div>
      <div className="page-header">
        <h1>用户画像</h1>
        <p>分析对话内容，提炼和保存用户基础信息和业务信息，构建完整的用户画像体系</p>
      </div>

      {/* 统计卡片 */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        {profileStats.map((stat, index) => (
          <Col xs={24} sm={12} lg={6} key={index}>
            <Card>
              <div className="stats-card">
                <div style={{ fontSize: 32, color: '#1890ff', marginBottom: 8 }}>
                  {stat.icon}
                </div>
                <div className="stats-number">{stat.value.toLocaleString()}</div>
                <div className="stats-label">{stat.title}</div>
              </div>
            </Card>
          </Col>
        ))}
      </Row>

      <Row gutter={[16, 16]}>
        {/* 用户列表 */}
        <Col xs={24} lg={16}>
          <Card
            title="用户画像列表"
            extra={
              <Space>
                <Tooltip title="用户行为分析">
                  <Button icon={<BarChartOutlined />} onClick={handleAnalyze}>
                    行为分析
                  </Button>
                </Tooltip>
                <Tooltip title="标签管理">
                  <Button icon={<TagOutlined />} onClick={() => setTagModalVisible(true)}>
                    标签管理
                  </Button>
                </Tooltip>
                <Tooltip title="导入用户数据">
                  <Button icon={<ImportOutlined />} onClick={handleImport}>
                    导入数据
                  </Button>
                </Tooltip>
                <Tooltip title="导出用户画像">
                  <Button type="primary" icon={<ExportOutlined />} onClick={handleExport}>
                    导出数据
                  </Button>
                </Tooltip>
              </Space>
            }
          >
            <div style={{ marginBottom: 16 }}>
              <Space>
                <Input
                  placeholder="搜索用户名或用户ID"
                  prefix={<SearchOutlined />}
                  style={{ width: 200 }}
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                />
                <Select 
                  placeholder="用户等级" 
                  style={{ width: 120 }}
                  value={filterGender}
                  onChange={setFilterGender}
                >
                  <Option value="all">全部等级</Option>
                  <Option value="普通用户">普通用户</Option>
                  <Option value="VIP1">VIP1</Option>
                  <Option value="VIP2">VIP2</Option>
                  <Option value="VIP3">VIP3</Option>
                </Select>
                <Select 
                  placeholder="风险等级" 
                  style={{ width: 120 }}
                  value={filterAge}
                  onChange={setFilterAge}
                >
                  <Option value="all">全部风险</Option>
                  <Option value="low">低风险</Option>
                  <Option value="medium">中风险</Option>
                  <Option value="high">高风险</Option>
                </Select>
                <Select 
                  placeholder="地区" 
                  style={{ width: 120 }}
                  value={filterLocation}
                  onChange={setFilterLocation}
                >
                  <Option value="all">全部地区</Option>
                  <Option value="北京">北京</Option>
                  <Option value="上海">上海</Option>
                  <Option value="广州">广州</Option>
                  <Option value="深圳">深圳</Option>
                </Select>
                <Tooltip title="刷新数据">
                  <Button 
                    icon={<ReloadOutlined />} 
                    onClick={() => message.info('数据已刷新')}
                  />
                </Tooltip>
                <Tooltip title="高级筛选">
                  <Button icon={<FilterOutlined />} />
                </Tooltip>
              </Space>
            </div>

            <Table
              rowSelection={rowSelection}
              columns={columns}
              dataSource={userProfileData}
              rowKey="id"
              pagination={{
                total: userProfileData.length,
                pageSize: 10,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total) => `共 ${total} 个用户`,
              }}
              scroll={{ x: 1200 }}
              size="middle"
            />

            {/* 批量操作 */}
            {selectedRowKeys.length > 0 && (
              <Card style={{ marginTop: 16 }}>
                <Space>
                  <span>已选择 {selectedRowKeys.length} 个用户</span>
                  <Tooltip title="批量导出">
                    <Button icon={<ExportOutlined />} onClick={handleExport}>
                      批量导出
                    </Button>
                  </Tooltip>
                  <Popconfirm
                    title={`确定要删除选中的 ${selectedRowKeys.length} 个用户画像吗？`}
                    onConfirm={handleBatchDelete}
                    okText="确定"
                    cancelText="取消"
                  >
                    <Tooltip title="批量删除">
                      <Button icon={<DeleteOutlined />} danger>
                        批量删除
                      </Button>
                    </Tooltip>
                  </Popconfirm>
                </Space>
              </Card>
            )}
          </Card>
        </Col>

        {/* 标签统计 */}
        <Col xs={24} lg={8}>
          <Card 
            title="用户标签统计" 
            extra={
              <Space>
                <Tooltip title="查看全部标签">
                  <Button type="link" onClick={() => message.info('查看全部标签功能开发中...')}>
                    查看全部
                  </Button>
                </Tooltip>
                <Tooltip title="添加新标签">
                  <Button type="link" icon={<PlusOutlined />} onClick={() => setTagModalVisible(true)}>
                    添加标签
                  </Button>
                </Tooltip>
              </Space>
            }
          >
            <List
              size="small"
              dataSource={tagStats}
              renderItem={(item) => (
                <List.Item
                  actions={[
                    <Tooltip title="编辑标签">
                      <Button type="link" size="small" onClick={() => message.info('编辑标签功能开发中...')}>
                        编辑
                      </Button>
                    </Tooltip>,
                    <Popconfirm
                      title="确定要删除这个标签吗？"
                      onConfirm={() => message.success(`已删除标签：${item.tag}`)}
                      okText="确定"
                      cancelText="取消"
                    >
                      <Tooltip title="删除标签">
                        <Button type="link" size="small" danger>
                          删除
                        </Button>
                      </Tooltip>
                    </Popconfirm>,
                  ]}
                >
                  <List.Item.Meta
                    avatar={<Tag color={item.color}>{item.tag}</Tag>}
                    title={
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span>{item.tag}</span>
                        <Badge count={item.count} style={{ backgroundColor: '#52c41a' }} />
                      </div>
                    }
                  />
                </List.Item>
              )}
            />
          </Card>

          <Card title="用户分层" style={{ marginTop: 16 }}>
            <div style={{ marginBottom: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                <span>VIP用户</span>
                <span>6.8%</span>
              </div>
              <Progress percent={6.8} strokeColor="#52c41a" />
            </div>
            <div style={{ marginBottom: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                <span>活跃用户</span>
                <span>25.7%</span>
              </div>
              <Progress percent={25.7} strokeColor="#1890ff" />
            </div>
            <div style={{ marginBottom: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                <span>普通用户</span>
                <span>67.5%</span>
              </div>
              <Progress percent={67.5} strokeColor="#faad14" />
            </div>
          </Card>
        </Col>
      </Row>

      {/* 详情模态框 */}
      <Modal
        title="用户画像详情"
        open={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        width={800}
        footer={[
          <Button key="close" onClick={() => setDetailModalVisible(false)}>
            关闭
          </Button>,
        ]}
      >
        {selectedRecord && (
          <Tabs defaultActiveKey="1">
            <Tabs.TabPane tab="基础信息" key="1">
              <Descriptions column={2}>
                <Descriptions.Item label="用户名">{selectedRecord.username}</Descriptions.Item>
                <Descriptions.Item label="用户ID">{selectedRecord.userId}</Descriptions.Item>
                <Descriptions.Item label="年龄">
                  {selectedRecord.age}
                  {selectedRecord.confidence && (
                    <ConfidenceIndicator confidence={selectedRecord.confidence.age} field="年龄" />
                  )}
                </Descriptions.Item>
                <Descriptions.Item label="性别">
                  {selectedRecord.gender}
                  {selectedRecord.confidence && (
                    <ConfidenceIndicator confidence={selectedRecord.confidence.gender} field="性别" />
                  )}
                </Descriptions.Item>
                <Descriptions.Item label="位置">
                  {selectedRecord.location}
                  {selectedRecord.confidence && (
                    <ConfidenceIndicator confidence={selectedRecord.confidence.location} field="位置" />
                  )}
                </Descriptions.Item>
                <Descriptions.Item label="学历">
                  {selectedRecord.education}
                  {selectedRecord.confidence && (
                    <ConfidenceIndicator confidence={selectedRecord.confidence.education} field="学历" />
                  )}
                </Descriptions.Item>
                <Descriptions.Item label="职业">
                  {selectedRecord.occupation}
                  {selectedRecord.confidence && (
                    <ConfidenceIndicator confidence={selectedRecord.confidence.occupation} field="职业" />
                  )}
                </Descriptions.Item>
                <Descriptions.Item label="用户等级">
                  <Tag color="blue">{selectedRecord.userLevel}</Tag>
                </Descriptions.Item>
                <Descriptions.Item label="注册时间">{selectedRecord.registrationTime}</Descriptions.Item>
                <Descriptions.Item label="最后活跃">{selectedRecord.lastActiveTime}</Descriptions.Item>
              </Descriptions>
              <Divider />
              <div>
                <h4>兴趣爱好</h4>
                <Space wrap>
                  {selectedRecord.interests.map((interest: string, index: number) => (
                    <Tag key={index}>{interest}</Tag>
                  ))}
                </Space>
              </div>
            </Tabs.TabPane>
            <Tabs.TabPane tab="业务信息" key="2">
              <Row gutter={[16, 16]}>
                <Col span={8}>
                  <Statistic title="总充值" value={selectedRecord.totalDeposit} prefix="¥" />
                </Col>
                <Col span={8}>
                  <Statistic title="总提现" value={selectedRecord.totalWithdraw} prefix="¥" />
                </Col>
                <Col span={8}>
                  <Statistic title="总投注" value={selectedRecord.totalBet} prefix="¥" />
                </Col>
              </Row>
              <Divider />
              <Descriptions column={2}>
                <Descriptions.Item label="在线时长">{selectedRecord.onlineTime}</Descriptions.Item>
                <Descriptions.Item label="平均会话时长">{selectedRecord.avgSessionTime}</Descriptions.Item>
                <Descriptions.Item label="偏好游戏">
                  <Space wrap>
                    {selectedRecord.favoriteGames.map((game: string, index: number) => (
                      <Tag key={index} color="purple">{game}</Tag>
                    ))}
                  </Space>
                </Descriptions.Item>
                <Descriptions.Item label="风险等级">
                  <Tag color={selectedRecord.riskLevel === 'low' ? 'green' : selectedRecord.riskLevel === 'medium' ? 'orange' : 'red'}>
                    {selectedRecord.riskLevel === 'low' ? '低风险' : selectedRecord.riskLevel === 'medium' ? '中风险' : '高风险'}
                  </Tag>
                </Descriptions.Item>
                <Descriptions.Item label="满意度">
                  <Progress percent={selectedRecord.satisfaction * 20} format={() => `${selectedRecord.satisfaction}/5`} />
                </Descriptions.Item>
              </Descriptions>
            </Tabs.TabPane>
            <Tabs.TabPane tab="用户标签" key="3">
              <div style={{ marginBottom: 16 }}>
                <h4>当前标签</h4>
                <Space wrap>
                  {selectedRecord.tags.map((tag: string, index: number) => (
                    <Tag key={index} color="green" closable>
                      {tag}
                    </Tag>
                  ))}
                </Space>
              </div>
              <div>
                <h4>推荐标签</h4>
                <Space wrap>
                  <Tag color="blue">游戏达人</Tag>
                  <Tag color="purple">高消费</Tag>
                  <Tag color="cyan">稳定用户</Tag>
                </Space>
              </div>
            </Tabs.TabPane>
            <Tabs.TabPane tab="对话总结" key="4">
              {selectedRecord.conversationBots && selectedRecord.conversationBots.length > 0 && (
                <div>
                  <Alert
                    message="对话Bot分析"
                    description="基于用户最近一周与不同类型AI机器人的对话记录生成的详细分析"
                    type="info"
                    showIcon
                    style={{ marginBottom: 16 }}
                  />
                  
                  {selectedRecord.conversationBots.map((bot: any, index: number) => (
                    <Card
                      key={index}
                      title={
                        <Space>
                          <RobotOutlined />
                          <span>{bot.botType}</span>
                          <Tag color="blue">交互 {bot.count} 次</Tag>
                        </Space>
                      }
                      style={{ marginBottom: 16 }}
                    >
                      <div style={{ marginBottom: 16 }}>
                        <h4 style={{ marginBottom: 8 }}>解决的问题：</h4>
                        <Space wrap>
                          {bot.solvedProblems.map((problem: string, idx: number) => (
                            <Tag color="green" key={idx}>{problem}</Tag>
                          ))}
                        </Space>
                      </div>
                      
                      <div>
                        <h4 style={{ marginBottom: 8 }}>Bot对话总结：</h4>
                        <div style={{ 
                          color: '#666', 
                          lineHeight: 1.8,
                          fontSize: 13,
                          background: '#f5f5f5',
                          padding: 12,
                          borderRadius: 4
                        }}>
                          {bot.summary}
                        </div>
                      </div>
                    </Card>
                  ))}
                  
                  <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid #f0f0f0' }}>
                    <Space>
                      <Button size="small" onClick={() => message.info('导出对话总结功能开发中...')}>
                        导出总结
                      </Button>
                      <Button size="small" onClick={() => message.info('编辑功能开发中...')}>
                        编辑总结
                      </Button>
                    </Space>
                  </div>
                </div>
              )}
            </Tabs.TabPane>
            <Tabs.TabPane tab="AI摘要分析" key="5">
              {selectedRecord.summary && (
                <div>
                  <Alert
                    message="AI分析摘要"
                    description="以下内容基于用户行为数据和AI算法分析生成，置信度仅供参考"
                    type="info"
                    showIcon
                    style={{ marginBottom: 16 }}
                  />
                  
                  <Card title="用户画像摘要" size="small" style={{ marginBottom: 16 }}>
                    <div style={{ marginBottom: 12 }}>
                      <h4>性格特征</h4>
                      <p style={{ color: '#666', lineHeight: 1.6 }}>{selectedRecord.summary.personality}</p>
                    </div>
                    <div style={{ marginBottom: 12 }}>
                      <h4>行为模式</h4>
                      <p style={{ color: '#666', lineHeight: 1.6 }}>{selectedRecord.summary.behavior}</p>
                    </div>
                    <div style={{ marginBottom: 12 }}>
                      <h4>偏好分析</h4>
                      <p style={{ color: '#666', lineHeight: 1.6 }}>{selectedRecord.summary.preferences}</p>
                    </div>
                    <div style={{ marginBottom: 12 }}>
                      <h4>风险画像</h4>
                      <p style={{ color: '#666', lineHeight: 1.6 }}>{selectedRecord.summary.riskProfile}</p>
                    </div>
                    <div>
                      <h4>价值评估</h4>
                      <p style={{ color: '#666', lineHeight: 1.6 }}>{selectedRecord.summary.value}</p>
                    </div>
                  </Card>

                  <Card title="置信度分析" size="small">
                    <Row gutter={16}>
                      <Col span={8}>
                        <div style={{ textAlign: 'center', padding: 16, background: '#f6ffed', borderRadius: 8 }}>
                          <div style={{ fontSize: 24, fontWeight: 'bold', color: '#52c41a' }}>
                            {selectedRecord.confidence ? (selectedRecord.confidence.age * 100).toFixed(0) : 0}%
                          </div>
                          <div style={{ color: '#666' }}>基础信息置信度</div>
                        </div>
                      </Col>
                      <Col span={8}>
                        <div style={{ textAlign: 'center', padding: 16, background: '#f0f9ff', borderRadius: 8 }}>
                          <div style={{ fontSize: 24, fontWeight: 'bold', color: '#1890ff' }}>
                            {selectedRecord.confidence ? (selectedRecord.confidence.riskLevel * 100).toFixed(0) : 0}%
                          </div>
                          <div style={{ color: '#666' }}>风险分析置信度</div>
                        </div>
                      </Col>
                      <Col span={8}>
                        <div style={{ textAlign: 'center', padding: 16, background: '#fff7e6', borderRadius: 8 }}>
                          <div style={{ fontSize: 24, fontWeight: 'bold', color: '#fa8c16' }}>
                            {selectedRecord.confidence ? (selectedRecord.confidence.satisfaction * 100).toFixed(0) : 0}%
                          </div>
                          <div style={{ color: '#666' }}>满意度置信度</div>
                        </div>
                      </Col>
                    </Row>
                  </Card>
                </div>
              )}
            </Tabs.TabPane>
          </Tabs>
        )}
      </Modal>

      {/* 标签管理模态框 */}
      <Modal
        title="标签管理"
        open={tagModalVisible}
        onCancel={() => setTagModalVisible(false)}
        width={600}
        footer={[
          <Button key="cancel" onClick={() => setTagModalVisible(false)}>
            取消
          </Button>,
          <Button key="save" type="primary">
            保存
          </Button>,
        ]}
      >
        <Tabs defaultActiveKey="1">
          <Tabs.TabPane tab="标签列表" key="1">
            <List
              dataSource={tagStats}
              renderItem={(item) => (
                <List.Item
                  actions={[
                    <Button type="link" size="small">编辑</Button>,
                    <Button type="link" size="small" danger>删除</Button>,
                  ]}
                >
                  <List.Item.Meta
                    avatar={<Tag color={item.color}>{item.tag}</Tag>}
                    title={item.tag}
                    description={`使用次数: ${item.count}`}
                  />
                </List.Item>
              )}
            />
          </Tabs.TabPane>
          <Tabs.TabPane tab="添加标签" key="2">
            <div style={{ marginBottom: 16 }}>
              <label>标签名称：</label>
              <Input placeholder="输入标签名称" style={{ width: 200, marginLeft: 8 }} />
            </div>
            <div style={{ marginBottom: 16 }}>
              <label>标签颜色：</label>
              <Select defaultValue="blue" style={{ width: 120, marginLeft: 8 }}>
                <Option value="red">红色</Option>
                <Option value="blue">蓝色</Option>
                <Option value="green">绿色</Option>
                <Option value="orange">橙色</Option>
                <Option value="purple">紫色</Option>
              </Select>
            </div>
            <div>
              <label>标签描述：</label>
              <Input.TextArea placeholder="输入标签描述" rows={3} />
            </div>
          </Tabs.TabPane>
        </Tabs>
      </Modal>

      {/* 用户行为分析模态框 */}
      <Modal
        title="用户行为分析"
        open={analysisModalVisible}
        onCancel={() => setAnalysisModalVisible(false)}
        width={1000}
        footer={[
          <Button key="close" onClick={() => setAnalysisModalVisible(false)}>
            关闭
          </Button>,
          <Button key="export" type="primary" icon={<ExportOutlined />}>
            导出分析报告
          </Button>
        ]}
      >
        <Tabs defaultActiveKey="1">
          <Tabs.TabPane tab="行为趋势" key="1">
            <Row gutter={[16, 16]}>
              <Col span={12}>
                <Card title="活跃度趋势" size="small">
                  <div style={{ height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <div style={{ textAlign: 'center' }}>
                      <LineChartOutlined style={{ fontSize: 48, color: '#1890ff' }} />
                      <p>活跃度趋势图</p>
                    </div>
                  </div>
                </Card>
              </Col>
              <Col span={12}>
                <Card title="使用时长分布" size="small">
                  <div style={{ height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <div style={{ textAlign: 'center' }}>
                      <PieChartOutlined style={{ fontSize: 48, color: '#52c41a' }} />
                      <p>时长分布饼图</p>
                    </div>
                  </div>
                </Card>
              </Col>
            </Row>
          </Tabs.TabPane>
          
          <Tabs.TabPane tab="用户分群" key="2">
            <div>
              <h4>用户分群分析</h4>
              <List
                dataSource={[
                  { group: '高价值用户', count: 1250, percentage: 15.2 },
                  { group: '活跃用户', count: 3200, percentage: 38.8 },
                  { group: '潜在用户', count: 2100, percentage: 25.4 },
                  { group: '流失风险用户', count: 1700, percentage: 20.6 },
                ]}
                renderItem={(item) => (
                  <List.Item>
                    <List.Item.Meta
                      title={item.group}
                      description={`用户数量: ${item.count} (${item.percentage}%)`}
                    />
                    <Progress percent={item.percentage} />
                  </List.Item>
                )}
              />
            </div>
          </Tabs.TabPane>
          
          <Tabs.TabPane tab="行为洞察" key="3">
            <div>
              <h4>关键洞察</h4>
              <List
                dataSource={[
                  '用户平均在线时长较上月增长12%',
                  'VIP用户转化率提升至8.5%',
                  '新用户留存率保持在65%以上',
                  '用户满意度评分达到4.2/5.0',
                ]}
                renderItem={(item) => (
                  <List.Item>
                    <List.Item.Meta
                      avatar={<Badge status="success" />}
                      description={item}
                    />
                  </List.Item>
                )}
              />
            </div>
          </Tabs.TabPane>
        </Tabs>
      </Modal>

      {/* 导入用户数据模态框 */}
      <Modal
        title="导入用户数据"
        open={importModalVisible}
        onCancel={() => setImportModalVisible(false)}
        width={600}
        footer={[
          <Button key="cancel" onClick={() => setImportModalVisible(false)}>
            取消
          </Button>,
          <Button key="import" type="primary" icon={<ImportOutlined />}>
            开始导入
          </Button>
        ]}
      >
        <div>
          <Alert
            message="导入说明"
            description="支持导入Excel、CSV格式的用户数据文件，请确保文件格式正确"
            type="info"
            style={{ marginBottom: 16 }}
          />
          
          <Upload.Dragger
            name="file"
            multiple={false}
            accept=".xlsx,.xls,.csv"
            beforeUpload={() => false}
          >
            <p className="ant-upload-drag-icon">
              <FileTextOutlined />
            </p>
            <p className="ant-upload-text">点击或拖拽文件到此区域上传</p>
            <p className="ant-upload-hint">支持单个文件上传，文件大小不超过10MB</p>
          </Upload.Dragger>
          
          <div style={{ marginTop: 16 }}>
            <h4>导入模板：</h4>
            <Button icon={<DownloadOutlined />} type="link">
              下载Excel模板
            </Button>
            <Button icon={<DownloadOutlined />} type="link">
              下载CSV模板
            </Button>
          </div>
        </div>
      </Modal>

      {/* 导出用户画像模态框 */}
      <Modal
        title="导出用户画像"
        open={exportModalVisible}
        onCancel={() => setExportModalVisible(false)}
        width={500}
        footer={[
          <Button key="cancel" onClick={() => setExportModalVisible(false)}>
            取消
          </Button>,
          <Button key="export" type="primary" icon={<ExportOutlined />}>
            开始导出
          </Button>
        ]}
      >
        <div>
          <Form layout="vertical">
            <Form.Item label="导出格式">
              <Radio.Group defaultValue="excel">
                <Radio value="excel">Excel (.xlsx)</Radio>
                <Radio value="csv">CSV (.csv)</Radio>
                <Radio value="json">JSON (.json)</Radio>
              </Radio.Group>
            </Form.Item>
            
            <Form.Item label="导出范围">
              <Radio.Group defaultValue="all">
                <Radio value="all">全部用户</Radio>
                <Radio value="selected">已选择的用户 ({selectedRowKeys.length} 个)</Radio>
                <Radio value="filtered">当前筛选结果</Radio>
              </Radio.Group>
            </Form.Item>
            
            <Form.Item label="包含字段">
              <Checkbox.Group defaultValue={['username', 'age', 'gender', 'location', 'userLevel']}>
                <Checkbox value="username">用户名</Checkbox>
                <Checkbox value="age">年龄</Checkbox>
                <Checkbox value="gender">性别</Checkbox>
                <Checkbox value="location">地区</Checkbox>
                <Checkbox value="userLevel">用户等级</Checkbox>
                <Checkbox value="totalDeposit">充值金额</Checkbox>
                <Checkbox value="totalBet">投注金额</Checkbox>
                <Checkbox value="onlineTime">在线时长</Checkbox>
                <Checkbox value="riskLevel">风险等级</Checkbox>
                <Checkbox value="satisfaction">满意度</Checkbox>
              </Checkbox.Group>
            </Form.Item>
          </Form>
        </div>
      </Modal>
    </div>
  );
};

export default UserProfile;
